/**
 * Sublishop - shared inventory pool guard
 * ---------------------------------------
 * Singles and multi-packs of the same product draw on ONE physical pool of
 * units. Shopify tracks each variant independently, so without this guard a
 * customer can cart 112 singles AND 11 packs of 10 (= 222 units) when only 112
 * units exist. Shopify Flow cannot prevent that: it only fires after the order
 * is placed.
 *
 * How it works
 *   - The "Single" variant's inventory is the source of truth for the pool.
 *   - Every variant is worth N units ("Single" = 1, "Pack of 10" = 10, ...).
 *   - Cart writes are intercepted. We work out what the cart WILL look like,
 *     and if a product's total units exceed its pool we undo the operation and
 *     return a normal Shopify-shaped error so the theme displays it.
 *
 * Performance
 *   The cart state is reconstructed from the cart response itself and the pool
 *   data is cached, so in normal use the guard adds no extra network round
 *   trips. A full /cart.js read only happens on a cold cart or to double-check
 *   before actually blocking something.
 *
 * Fails OPEN: any network/parse problem lets the operation through, so a hiccup
 * can never block sales. Shopify's own per-variant stock check still applies.
 */
(function () {
  'use strict';

  var POOL_TTL = 60000; // ms to cache a product's pool data
  var CART_TTL = 10000; // ms to trust our reconstructed cart
  var OPTION_NAME = /pack\s*size/i;

  var poolCache = new Map();
  var cartCache = { at: 0, items: null };
  var nativeFetch = window.fetch.bind(window);

  /* ------------------------------------------------------------------ utils */

  function rootPath() {
    // Respects locale/market subpaths e.g. /en-gb/
    var r = (window.theme && theme.routes && theme.routes.cart) || '/cart';
    return r.replace(/\/cart$/, '');
  }

  function isCartWrite(url) {
    return /\/cart\/(add|change|update)(\.js)?($|\?)/.test(url);
  }

  /**
   * How many physical units one of this variant represents.
   * "Single" -> 1, "Pack of 10" -> 10, "Pack of 5" -> 5.
   */
  function unitsFor(label) {
    if (!label) return 1;
    var m = String(label).match(/(\d+)/);
    return m ? Math.max(1, parseInt(m[1], 10)) : 1;
  }

  /* ------------------------------------------------------------- pool lookup */

  function fetchPool(handle) {
    var cached = poolCache.get(handle);
    if (cached && Date.now() - cached.at < POOL_TTL) return cached.promise;

    var promise = nativeFetch(rootPath() + '/products/' + handle + '?view=pack-stock')
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (text) {
        return buildPool(JSON.parse(text));
      })
      .catch(function () {
        return null; // fail open
      });

    poolCache.set(handle, { at: Date.now(), promise: promise });
    return promise;
  }

  /**
   * Turn the raw ?view=pack-stock payload into { pool, units: {variantId: n} }
   * or null when the product has no Pack Size option (the vast majority).
   */
  function buildPool(data) {
    if (!data || !Array.isArray(data.options)) return null;

    var idx = -1;
    data.options.forEach(function (name, i) {
      if (idx === -1 && OPTION_NAME.test(name)) idx = i;
    });
    if (idx === -1) return null;

    var units = {};
    var pool = null;

    data.variants.forEach(function (v) {
      var label = Array.isArray(v.options) ? v.options[idx] : null;
      var n = unitsFor(label);
      units[v.id] = n;

      // The 1-unit variant holds the real stock figure for the whole pool.
      if (n === 1) {
        pool = v.tracked ? v.qty : Infinity;
      }
    });

    // No "Single" variant means there is no pool to share - leave it to Shopify.
    if (pool === null) return null;

    return { pool: pool, units: units };
  }

  /* --------------------------------------------------------------- cart state */

  function getCart() {
    return nativeFetch(rootPath() + '/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then(function (cart) {
        remember(cart.items);
        return cart.items;
      });
  }

  function remember(items) {
    cartCache = { at: Date.now(), items: items };
  }

  function knownCart() {
    if (cartCache.items && Date.now() - cartCache.at < CART_TTL) {
      return Promise.resolve(cartCache.items);
    }
    return getCart();
  }

  /**
   * Work out the resulting cart without another network call.
   *  - /cart/change.js and /cart/update.js return the whole cart already.
   *  - /cart/add.js returns only what was added, so merge it into what we had.
   */
  function projectCart(before, data) {
    if (!data) return null;

    // A full cart object always carries item_count.
    if (typeof data.item_count === 'number' && Array.isArray(data.items)) {
      return data.items;
    }

    var added = Array.isArray(data.items) ? data.items
      : (data.variant_id || data.id) ? [data] : null;
    if (!added || !before) return null;

    var merged = before.map(function (i) {
      return { handle: i.handle, variant_id: i.variant_id, quantity: i.quantity, product_title: i.product_title };
    });

    added.forEach(function (a) {
      var vid = a.variant_id || a.id;
      var line = null;
      merged.forEach(function (m) { if (!line && m.variant_id === vid) line = m; });
      if (line) {
        line.quantity += a.quantity;
      } else {
        merged.push({ handle: a.handle, variant_id: vid, quantity: a.quantity, product_title: a.product_title });
      }
    });

    return merged;
  }

  /* -------------------------------------------------------------- validation */

  /**
   * Returns a violation descriptor for the first over-committed product, or
   * null when the cart is valid.
   */
  function findViolation(items) {
    if (!items) return Promise.resolve(null);

    var handles = [];
    items.forEach(function (item) {
      if (item.handle && handles.indexOf(item.handle) === -1) handles.push(item.handle);
    });
    if (!handles.length) return Promise.resolve(null);

    return Promise.all(handles.map(fetchPool)).then(function (pools) {
      for (var i = 0; i < handles.length; i++) {
        var info = pools[i];
        if (!info || info.pool === Infinity) continue;

        var handle = handles[i];
        var used = 0;
        var title = '';

        items.forEach(function (item) {
          if (item.handle !== handle) return;
          used += item.quantity * (info.units[item.variant_id] || 1);
          title = item.product_title || title;
        });

        if (used > info.pool) {
          return { handle: handle, title: title, used: used, pool: info.pool };
        }
      }
      return null;
    });
  }

  function message(v) {
    if (v.pool <= 0) return v.title + ' is out of stock.';
    return 'Only ' + v.pool + ' of ' + v.title + ' are available in total. '
      + 'Singles and packs come from the same stock, so your cart is asking for '
      + v.used + '. Please reduce the quantity.';
  }

  function errorResponse(v) {
    var msg = message(v);
    return new Response(
      JSON.stringify({
        status: 422,
        message: 'Not enough stock',
        description: msg,
        errors: msg
      }),
      { status: 422, headers: { 'Content-Type': 'application/json' } }
    );
  }

  /* ---------------------------------------------------------------- rollback */

  /** Restore the quantities captured before the operation, for one product. */
  function rollback(before, handle) {
    var updates = {};
    before.forEach(function (item) {
      if (item.handle === handle) updates[item.variant_id] = item.quantity;
    });

    // Lines of this product created by the blocked operation are not in the
    // snapshot, so zero them explicitly.
    return getCart().then(function (items) {
      items.forEach(function (item) {
        if (item.handle === handle && !(item.variant_id in updates)) {
          updates[item.variant_id] = 0;
        }
      });

      return nativeFetch(rootPath() + '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function (r) {
        return r.json();
      }).then(function (cart) {
        if (cart && cart.items) remember(cart.items);
      });
    });
  }

  /* ------------------------------------------------------------- interceptor */

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!isCartWrite(url)) return nativeFetch(input, init);

    var before = null;

    return knownCart()
      .then(
        function (items) { before = items; },
        function () { before = null; }
      )
      .then(function () {
        // The real request runs exactly once, here.
        return nativeFetch(input, init);
      })
      .then(function (response) {
        if (!response.ok || !before) return response;

        var clone;
        try { clone = response.clone(); } catch (e) { return response; }

        return clone.json()
          .then(function (data) {
            var projected = projectCart(before, data);
            if (!projected) return getCart(); // fall back to the real thing
            return projected;
          })
          .then(function (items) {
            return findViolation(items).then(function (violation) {
              if (!violation) {
                remember(items);
                return response;
              }
              // Only pay for a definitive read when we are about to block.
              return getCart()
                .then(findViolation)
                .then(function (confirmed) {
                  if (!confirmed) return response;
                  return rollback(before, confirmed.handle)
                    .catch(function () { /* best effort */ })
                    .then(function () {
                      poolCache.delete(confirmed.handle);
                      return errorResponse(confirmed);
                    });
                });
            });
          })
          .catch(function () {
            return response; // fail open
          });
      });
  };
})();
